import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Zap } from "lucide-react";

/**
 * SocialProofToast — shows a subtle notification when someone completes a session.
 * Displays one notification every 30-60 seconds to create FOMO without being annoying.
 */
export default function SocialProofToast() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");
  const { data: activity } = trpc.socialProof.recentActivity.useQuery(undefined, {
    refetchInterval: 60000, // Refresh every 60s
  });

  useEffect(() => {
    if (!activity || activity.length === 0) return;

    // Show a random notification after a delay
    const timer = setTimeout(() => {
      const random = activity[Math.floor(Math.random() * activity.length)];
      if (random?.score) {
        const timeAgo = getTimeAgo(Number(random.completedAt));
        setMessage(`Someone just scored ${random.score}/100 ${timeAgo}`);
        setVisible(true);

        // Hide after 4 seconds
        setTimeout(() => setVisible(false), 4000);
      }
    }, 15000 + Math.random() * 30000); // Show between 15-45s after load

    return () => clearTimeout(timer);
  }, [activity]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 left-6 z-40 animate-in slide-in-from-left-5 fade-in duration-300">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border/50 rounded-lg shadow-lg">
        <Zap className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-xs text-muted-foreground">{message}</span>
      </div>
    </div>
  );
}

function getTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return "recently";
}
